import type DocumentTemplateVersionDetailView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateVersionDetailView';
import type DocumentTemplateVersionView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateVersionView';
import DocumentTemplateVersionDeclaration from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateHistorySidebar/DocumentTemplateVersionRow/DocumentTemplateVersionDeclaration';
import { css } from '@emotion/react';
import * as React from 'react';
import { Message, Spinner, Tag } from '@verclam/ui-kit';

const depositDateFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' });

const versionRowStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	flex-wrap: wrap;
	width: 100%;
	text-align: left;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	font: inherit;
	color: inherit;
`;

const versionDetailStyle = css`
	margin-top: 0.5rem;
`;

const loadingRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

type Props = {
	readonly version: DocumentTemplateVersionView;
	readonly isCurrent: boolean;
	readonly isExpanded: boolean;
	readonly isLoadingDeclaration: boolean;
	readonly declarationError: string | null;
	readonly declaration: DocumentTemplateVersionDetailView | null;
	readonly onToggle: (versionId: number) => void;
};

/** One row of the version history; a leaf that dispatches nothing itself — `onToggle` (already bound
 * to `useDispatch` one level up) decides whether to fetch the declaration. */
const DocumentTemplateVersionRow: React.FC<Props> = ({
	version,
	isCurrent,
	isExpanded,
	isLoadingDeclaration,
	declarationError,
	declaration,
	onToggle,
}) => {
	const handleClick = React.useCallback(() => {
		onToggle(version.id);
	}, [onToggle, version.id]);

	return (
		<li>
			<button type="button" css={versionRowStyle} onClick={handleClick}>
				<span>
					Version {version.versionNumber} · déposée le{' '}
					{depositDateFormatter.format(new Date(version.createdAt))} · {version.fieldCount}{' '}
					variables · {version.originalFilename}
				</span>
				{isCurrent ? <Tag value="En vigueur" severity="success" /> : null}
			</button>
			{isExpanded ? (
				<div css={versionDetailStyle}>
					{isLoadingDeclaration ? (
						<div css={loadingRowStyle}>
							<Spinner aria-label="Chargement de la version" />
							<span>Chargement de la version…</span>
						</div>
					) : declarationError !== null ? (
						<Message severity="error" text={declarationError} />
					) : declaration !== null ? (
						<DocumentTemplateVersionDeclaration version={declaration} />
					) : null}
				</div>
			) : null}
		</li>
	);
};

export default DocumentTemplateVersionRow;
